"use client"

import type { TierFeature } from "../lib/pricing"
import { Check } from "lucide-react"
import { useState } from "react"

import { cn } from "@/lib/utils"

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"

// The landing pricing block runs a slightly roomier scale than the in-app
// billing cards; everything else about the list is identical.
const SIZES = {
  sm: { list: "space-y-1.5 text-sm", icon: "size-3.5" },
  md: { list: "space-y-2.5 text-sm", icon: "size-4" },
} as const

type TierFeatureListProps = {
  features: TierFeature[]
  size?: keyof typeof SIZES
  className?: string
}

// Features carrying a `detail` get a dotted underline and open a hover card.
// It is controlled rather than left to Radix so a tap works on touch devices,
// where there is no hover at all.
function FeatureLabel({ feature }: { feature: TierFeature }) {
  const [open, setOpen] = useState(false)

  if (!feature.detail) {
    return <span className="text-muted-foreground">{feature.label}</span>
  }

  return (
    <HoverCard
      open={open}
      onOpenChange={setOpen}
      openDelay={120}
      closeDelay={80}
    >
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((previous) => !previous)}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring cursor-help text-left underline decoration-current decoration-dotted decoration-2 underline-offset-4 transition-colors focus-visible:ring-2 focus-visible:outline-none"
        >
          {feature.label}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        tone="muted"
        align="start"
        className="text-2sm w-72 leading-relaxed"
      >
        {feature.detail}
      </HoverCardContent>
    </HoverCard>
  )
}

export function TierFeatureList({
  features,
  size = "sm",
  className,
}: TierFeatureListProps) {
  const sizing = SIZES[size]

  return (
    <ul className={cn("flex-1", sizing.list, className)}>
      {features.map((feature) => (
        <li key={feature.label} className="flex items-start gap-2">
          <Check
            className={cn("text-primary-cta mt-0.5 shrink-0", sizing.icon)}
            aria-hidden
          />
          <FeatureLabel feature={feature} />
        </li>
      ))}
    </ul>
  )
}
